import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { ExportButton, type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useComparisonMode } from '@/modules/analytics/hooks/useComparisonMode';
import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import { AbsoluteChangeIndicator } from './AbsoluteChangeIndicator';
import { AnalyticsEmptyState } from './AnalyticsEmptyState';
import { CsatStars } from './CsatStars';
import { useAiAgentsInsights } from './hooks/useAiAgentsInsights';
import { getComparisonPeriodKey } from './PercentChangeIndicator';
import { formatCsatDelta } from '../../utils/deltaFormatters';
import { formatDateRangeLabel } from '../../utils/formatDateRangeLabel';
import { formatScore } from '../../utils/formatScore';

const CSAT_MAX = 5;
const CSAT_DELTA_PRECISION = 1;

// The hook coerces a missing average to 0, and CSAT is scored 1–5, so a 0 means
// "no samples" rather than a genuine score. Map it back to null so the compare
// indicator reads "N/A" instead of measuring this period against a zero that
// never happened.
const toComparableScore = (value: number): number | null => (value > 0 ? value : null);

type CsatScoreSectionProps = {
  label: string;
  tooltip: string;
  score: number;
  ratedCount: number;
  totalConversations: number;
  /** Tailwind text-colour class for the filled stars. */
  starColorClassName: string;
  isLoading: boolean;
  comparison?: ReactNode;
};

/**
 * One score block: label, stars + value, optional compare delta, and the share
 * of conversations that carry this kind of score.
 *
 * A score with no samples renders the period empty-state text instead of a
 * fabricated "0.0" — the two kinds are independent, so an account can have user
 * ratings with no AI scores or the reverse.
 */
const CsatScoreSection = ({
  label,
  tooltip,
  score,
  ratedCount,
  totalConversations,
  starColorClassName,
  isLoading,
  comparison,
}: CsatScoreSectionProps) => {
  const { t } = useTranslation();
  const hasScore = ratedCount > 0 && score > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <span className="text-sm leading-4 font-semibold text-foreground">{label}</span>
        <InfoTooltip content={tooltip} size="large">
          <InfoIcon className="size-3.5 shrink-0 text-muted-foreground" />
        </InfoTooltip>
      </div>

      {isLoading && <Skeleton className="h-6 w-48" />}

      {!isLoading && !hasScore && (
        <p className="text-sm leading-5 text-muted-foreground">
          {t('analytics.no_data_for_period')}
        </p>
      )}

      {!isLoading && hasScore && (
        <>
          <div
            className="flex items-center gap-4"
            role="img"
            aria-label={t('analytics.csat_analysis.score_a11y', {
              0: label,
              1: formatScore(score),
              2: String(CSAT_MAX),
            })}
          >
            <CsatStars score={score} colorClassName={starColorClassName} />
            <span className="text-2xl leading-6 font-bold tracking-tight text-foreground">
              {formatScore(score)}
            </span>
          </div>

          {comparison}

          <div className="flex items-baseline justify-between gap-2 border-l border-border pl-3 text-sm leading-5">
            <span className="text-muted-foreground">
              {t('analytics.csat_analysis.conversations_rated')}
            </span>
            <span className="font-medium text-foreground">
              {`${ratedCount.toLocaleString()} / ${totalConversations.toLocaleString()}`}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

type CsatScoreAnalysisCardProps = { className?: string };

/**
 * CSAT score analysis card for the AI Agents analytics page.
 *
 * Splits the two satisfaction scores the platform tracks so a number is never
 * ambiguous: the User CSAT score comes from end users who rated the
 * conversation, the AI CSAT score is inferred per conversation by the LLM.
 * The two populations overlap, so each carries its own rated count.
 */
export const CsatScoreAnalysisCard = ({ className }: CsatScoreAnalysisCardProps) => {
  const { t } = useTranslation();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);
  const { isComparing } = useComparisonMode();
  const [isExporting, setIsExporting] = useState(false);
  const { data, isLoading, isEmpty: isOverviewEmpty } = useAiAgentsInsights();

  const dateRange = useMemo(() => formatDateRangeLabel(startDate, endDate), [startDate, endDate]);
  const comparisonLabel = t(getComparisonPeriodKey(startDate, endDate));

  const { csatBreakdown, comparisonCsatBreakdown } = data;
  const totalConversations = data.kpiValues.totalConversations.current;

  // While loading every value is the hook's null-safe `0`; don't flash the
  // empty state until the query resolves.
  const isEmpty = !isLoading && isOverviewEmpty;

  const handleExport = useCallback(
    (exportFormat: ExportFormat) => {
      try {
        setIsExporting(true);
        const filename = buildExportFilename('csat_score_analysis');
        // A zero average means "no ratings", not a worst-possible score —
        // export null so the file never fabricates a rating.
        const rows = [
          {
            type: 'user',
            score: csatBreakdown.userRatingCount ? csatBreakdown.avgUserRating : null,
            conversations_rated: csatBreakdown.userRatingCount,
            total_conversations: totalConversations,
          },
          {
            type: 'ai',
            score: csatBreakdown.aiInsightsCount ? csatBreakdown.avgAiInsights : null,
            conversations_rated: csatBreakdown.aiInsightsCount,
            total_conversations: totalConversations,
          },
        ];

        if (exportFormat === 'json') {
          downloadJson({ csat_score_analysis: rows }, filename);
        } else {
          downloadCsv(rows, filename);
        }
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [csatBreakdown, totalConversations, t]
  );

  return (
    <Card
      className={cn(
        'flex h-full min-h-0 flex-col rounded-lg bg-background text-foreground shadow-none',
        className
      )}
    >
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-2">
            <CardTitle className="truncate">{t('analytics.csat_analysis.title')}</CardTitle>
            <CardDescription className="truncate">{dateRange}</CardDescription>
          </div>
          {!isEmpty && (
            <ExportButton
              variant="icon"
              onExport={handleExport}
              isExporting={isExporting}
              disabled={isLoading}
            />
          )}
        </div>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col">
        {isEmpty ? (
          <div className="flex flex-1 items-center justify-center">
            <AnalyticsEmptyState
              title={t('analytics.no_data_available')}
              description={t('analytics.no_data_for_period')}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <CsatScoreSection
              label={t('analytics.csat_analysis.user_score')}
              tooltip={t('analytics.csat_analysis.user_score_tooltip')}
              score={csatBreakdown.avgUserRating}
              ratedCount={csatBreakdown.userRatingCount}
              totalConversations={totalConversations}
              starColorClassName="text-chart-1"
              isLoading={isLoading}
              comparison={
                isComparing ? (
                  <AbsoluteChangeIndicator
                    current={csatBreakdown.avgUserRating}
                    previous={toComparableScore(comparisonCsatBreakdown.avgUserRating)}
                    formatDelta={formatCsatDelta}
                    precision={CSAT_DELTA_PRECISION}
                    comparisonLabel={comparisonLabel}
                  />
                ) : undefined
              }
            />

            <CsatScoreSection
              label={t('analytics.csat_analysis.ai_score')}
              tooltip={t('analytics.csat_analysis.ai_score_tooltip')}
              score={csatBreakdown.avgAiInsights}
              ratedCount={csatBreakdown.aiInsightsCount}
              totalConversations={totalConversations}
              starColorClassName="text-chart-5"
              isLoading={isLoading}
              comparison={
                isComparing ? (
                  <AbsoluteChangeIndicator
                    current={csatBreakdown.avgAiInsights}
                    previous={toComparableScore(comparisonCsatBreakdown.avgAiInsights)}
                    formatDelta={formatCsatDelta}
                    precision={CSAT_DELTA_PRECISION}
                    comparisonLabel={comparisonLabel}
                  />
                ) : undefined
              }
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
